/*
 * <jj-concession-picker>
 *
 * Drives BOTH concession choices on the Movie Night product page:
 *
 *   1. the INCLUDED machine - a line item property on the package line, no
 *      price, no cart line of its own
 *   2. the optional PAID SECOND machine - a real cart line for the
 *      second-concession-machine-add-on variant at its real Shopify price
 *
 * Both belong to the package tiers that include a customer-chosen machine
 * (Premium Plus and Deluxe), and the whole element must be completely inert
 * for every other variant.
 *
 * Why it is written this way
 * --------------------------
 * Spotlight's product-info.js does not re-render the whole product section on
 * a variant change. It patches price, SKU, inventory and the variant picker in
 * place and then publishes PUB_SUB_EVENTS.variantChange with the parsed
 * document and the newly selected variant. This element therefore stays
 * mounted across variant changes and re-derives its own state from that event,
 * the same pattern rental-availability.js already uses. When Shopify swaps the
 * whole product (a different product URL) product-info replaces its own
 * subtree, the element is torn down and connectedCallback runs again.
 *
 * Stale values
 * ------------
 * Hiding the picker is not enough - a hidden but enabled input is still
 * submitted. Whenever a non-trigger variant is selected the included inputs
 * are unchecked AND disabled, so a Basic or Standard package can never carry a
 * concession property into the cart. The second-concession radios are cleared
 * and disabled at the same time, so a paid line can never survive a switch to
 * a package that does not offer it.
 *
 * 2026-07-29: the validation message is driven by a class rather than the
 * hidden attribute alone, so nothing else in the theme can suppress it.
 *
 * 2026-08-03: the message is shown in ONE place only - this element's own
 * error line, directly beneath the choices.
 *
 * 2026-08-29: more than one trigger variant, so Deluxe inherits Premium Plus's
 * customer-choice concession. The comparison is a membership test against
 * data-trigger-variants, with the original singular data-trigger-variant read
 * as a fallback.
 *
 * ---------------------------------------------------------------------------
 * 2026-08-29b: the paid second concession.
 * Backup of the previous revision: assets/jj-backup-20260829b-jj-concession-picker.js
 *
 * HOW THE SECOND MACHINE REACHES THE CART
 * It cannot come from the product form - assets/product-form.js posts one
 * FormData with one id. <jj-couch-stepper> already intercepts the submit and
 * posts a multi-item /cart/add.js, and two capture-phase interceptors on one
 * form would fight over stopImmediatePropagation() and could produce two
 * non-atomic adds.
 *
 * So this element does NOT intercept anything for the second machine. It
 * registers a provider with window.JJCartExtras and the couch stepper folds
 * the line into the single atomic call it already makes. The provider also
 * carries the "you must choose an included machine" guard, because the couch
 * stepper's stopImmediatePropagation() would otherwise skip this element's own
 * submit listener whenever couches were involved - a latent bug that predates
 * the second concession.
 *
 * FAIL SAFE
 * If the couch stepper is absent or has disabled itself (its extra-futon
 * variant went missing, say) nothing would post the extra line, and a customer
 * who had selected a second machine would silently not get it. onSubmitCapture
 * therefore blocks the add in that specific case and says so, rather than
 * letting the request go through incomplete. The check is written so that it
 * cannot misfire when the stepper is healthy, regardless of which element
 * registered its document listener first.
 * ---------------------------------------------------------------------------
 */

if (!customElements.get('jj-concession-picker')) {
  customElements.define(
    'jj-concession-picker',
    class JjConcessionPicker extends HTMLElement {
      constructor() {
        super();
        this.variantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.cartErrorUnsubscriber = undefined;
        this.submitLocked = false;
        this.submitLockTimer = undefined;
        this.defaultErrorText = '';

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.releaseSubmitLock = this.releaseSubmitLock.bind(this);
      }

      connectedCallback() {
        const errorElement = this.errorElement;
        this.defaultErrorText = errorElement ? errorElement.textContent : '';

        this.addEventListener('change', this.onOptionChange);
        this.addEventListener('click', this.onClick);

        /*
         * Bound on the document in the capture phase on purpose. <product-form>
         * binds its own submit handler directly to the form element, so a
         * listener on the form itself would only be guaranteed to run first if
         * it were registered first. Capturing from an ancestor always runs
         * before any listener on the target.
         */
        document.addEventListener('submit', this.onSubmitCapture, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );

          if (PUB_SUB_EVENTS.cartUpdate) {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.releaseSubmitLock);
          }
          if (PUB_SUB_EVENTS.cartError) {
            this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.releaseSubmitLock);
          }
        }

        this.registerCartExtras();

        // Derive the opening state from the variant the page actually rendered.
        this.applyVariantState(this.currentVariantId);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onOptionChange);
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('submit', this.onSubmitCapture, true);

        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
        if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
        if (typeof this.cartErrorUnsubscriber === 'function') this.cartErrorUnsubscriber();

        if (window.JJCartExtras && this.cartExtrasProvider) {
          window.JJCartExtras.unregister(this.cartExtrasProvider);
        }

        clearTimeout(this.submitLockTimer);
      }

      /* ---------------- Lookups ---------------- */

      /*
       * Every variant this picker applies to. Reads the plural attribute
       * first and falls back to the original singular one, so the element
       * works against either revision of the snippet.
       */
      get triggerVariantIds() {
        const raw = String(this.dataset.triggerVariants || this.dataset.triggerVariant || '');

        return raw
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id !== '');
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get formElement() {
        const id = this.dataset.formId;
        const byId = id ? document.getElementById(id) : null;
        if (byId) return byId;
        // Fallback in case the form id ever stops matching the section id.
        return this.productInfo?.querySelector('product-form form') || null;
      }

      get inputs() {
        return Array.from(this.querySelectorAll('[data-jj-cp-input]'));
      }

      get checkedInput() {
        return this.inputs.find((input) => input.checked) || null;
      }

      get errorElement() {
        return this.querySelector('[data-jj-cp-error]');
      }

      get isActive() {
        return !this.hidden;
      }

      /* ---- second concession ---- */

      get addonVariantId() {
        return parseInt(this.dataset.addonVariant || '0', 10) || 0;
      }

      get secondWrapper() {
        return this.querySelector('[data-jj-cp2]');
      }

      get secondInputs() {
        return Array.from(this.querySelectorAll('[data-jj-cp2-input]'));
      }

      get secondCheckedInput() {
        return this.secondInputs.find((input) => input.checked && !input.disabled) || null;
      }

      get currentVariantId() {
        const input = this.formElement?.querySelector('input[name="id"]');
        if (input && input.value) return String(input.value);

        const selected = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (selected) {
          try {
            return String(JSON.parse(selected.innerHTML)?.id ?? '');
          } catch (error) {
            return '';
          }
        }

        return '';
      }

      /*
       * Whether something on this page is actually capable of posting a
       * multi-item add. <jj-couch-stepper> sets its own hidden attribute when
       * it disables itself, so a visible stepper plus a live registry is the
       * honest test.
       */
      get submitterAvailable() {
        if (!window.JJCartExtras) return false;
        return !!document.querySelector('jj-couch-stepper:not([hidden])');
      }

      /* ---------------- Variant handling ---------------- */

      onVariantChange(event) {
        const data = event?.data;
        if (!data) return;

        // Ignore events published by a different product section on the page.
        const productInfo = this.productInfo;
        const ownSectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;
        if (data.sectionId && ownSectionId && data.sectionId !== ownSectionId) return;

        this.applyVariantState(data.variant ? String(data.variant.id) : '');
      }

      applyVariantState(variantId) {
        const active = variantId !== '' && this.triggerVariantIds.indexOf(variantId) !== -1;

        if (active) {
          this.hidden = false;
          this.inputs.forEach((input) => {
            input.disabled = false;
          });
        } else {
          this.hidden = true;
          // Unchecked AND disabled - a hidden but enabled input still submits.
          this.inputs.forEach((input) => {
            input.checked = false;
            input.disabled = true;
          });
        }

        this.hideError();
        this.syncMirrorInput();
        this.syncSecond();
      }

      onOptionChange(event) {
        const target = event.target;
        if (!target || typeof target.matches !== 'function') return;

        if (target.matches('[data-jj-cp-input]')) {
          // The shared name already makes these mutually exclusive. Enforced
          // again so only ever one value can leave this element.
          this.inputs.forEach((input) => {
            if (input !== target) input.checked = false;
          });

          this.hideError();
          this.syncMirrorInput();
          // The included machine changed, so a second choice that now
          // duplicates it has to be cleared.
          this.syncSecond();
          return;
        }

        if (target.matches('[data-jj-cp2-input]')) {
          this.secondInputs.forEach((input) => {
            if (input !== target) input.checked = false;
          });

          this.hideError();
          this.syncSecond();
        }
      }

      onClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (!target.closest('[data-jj-cp2-clear]')) return;

        event.preventDefault();
        this.clearSecond();
      }

      /* ---------------- Second concession ---------------- */

      clearSecond() {
        this.secondInputs.forEach((input) => {
          input.checked = false;
        });
        this.syncSecond();
      }

      /*
       * Single source of truth for the second-concession block. Safe to call
       * repeatedly, and always called after anything that could invalidate the
       * current selection.
       */
      syncSecond() {
        const wrapper = this.secondWrapper;
        if (!wrapper) return;

        const included = this.isActive ? this.checkedInput : null;
        const includedHandle = included ? included.dataset.jjCpHandle || '' : '';

        // "Add a SECOND machine" is meaningless before there is a first one.
        const show = this.isActive && !!included && !!this.addonVariantId;
        wrapper.hidden = !show;

        this.secondInputs.forEach((input) => {
          const duplicate = includedHandle !== '' && input.dataset.jjCp2Handle === includedHandle;

          if (!show || duplicate) {
            // Clearing as well as disabling: this is what auto-clears a second
            // choice that the customer's new included choice just invalidated.
            input.checked = false;
            input.disabled = true;
          } else {
            input.disabled = false;
          }

          const option = input.closest('[data-jj-cp2-option]');
          if (option) option.hidden = duplicate;
        });

        const chosen = this.secondCheckedInput;

        const clearButton = this.querySelector('[data-jj-cp2-clear]');
        if (clearButton) clearButton.hidden = !chosen;

        const status = this.querySelector('[data-jj-cp2-status]');
        if (status) {
          status.textContent = chosen
            ? chosen.value + ' will be added to your event as a second machine.'
            : '';
        }
      }

      /* ---------------- Cart payload ----------------
       * The included radios carry a form="..." attribute, so the browser
       * submits them with the product form even though they sit outside it. If
       * that association ever fails, a single hidden input inside the form
       * carries the value instead. Only one of the two is ever present, so the
       * property can never be submitted twice.
       *
       * The second-concession radios have no form attribute at all and are
       * never submitted. They only feed the JJCartExtras provider below.
       */

      syncMirrorInput() {
        const form = this.formElement;
        if (!form) return;

        const existing = form.querySelector('input[data-jj-cp-mirror]');
        const checked = this.isActive ? this.checkedInput : null;
        const needsMirror = !!checked && checked.form !== form;

        if (!needsMirror) {
          if (existing) existing.remove();
          return;
        }

        const mirror = existing || document.createElement('input');
        if (!existing) {
          mirror.type = 'hidden';
          mirror.name = 'properties[Concession Choice]';
          mirror.setAttribute('data-jj-cp-mirror', '');
          form.appendChild(mirror);
        }
        mirror.value = checked.value;
      }

      registerCartExtras() {
        if (!window.JJCartExtras) {
          console.error('Concession picker: JJCartExtras missing, the paid second machine cannot be offered.');
          return;
        }

        const self = this;

        this.cartExtrasProvider = {
          items: function (lineKey) {
            if (!self.isActive) return [];

            const chosen = self.secondCheckedInput;
            if (!chosen || !self.addonVariantId) return [];

            const properties = {
              Machine: chosen.value,
              Supplies: chosen.dataset.jjCp2Supplies || ''
            };

            // Attribution to the package line this was chosen alongside.
            // Underscore-prefixed, so rental-request-summary.liquid keeps it
            // out of everything customer facing.
            if (lineKey && lineKey !== '__jj_probe__') {
              properties._package_line_key = lineKey;
            }

            return [
              {
                id: self.addonVariantId,
                quantity: 1,
                properties: properties
              }
            ];
          },

          validate: function () {
            if (!self.isActive) return null;

            if (!self.checkedInput) {
              self.showError();
              return { message: 'Choose one included concession machine.' };
            }

            return null;
          },

          reset: function () {
            self.clearSecond();
          }
        };

        window.JJCartExtras.register(this.cartExtrasProvider);
      }

      /* ---------------- Submit guard ---------------- */

      onSubmitCapture(event) {
        const form = this.formElement;
        if (!form || event.target !== form) return;

        // Prevent a second add while the first request is still in flight.
        if (this.submitLocked) {
          this.blockSubmit(event);
          return;
        }

        if (this.isActive && !this.checkedInput) {
          this.blockSubmit(event);
          this.showError();
          return;
        }

        /*
         * A paid second machine is selected but nothing on this page can post
         * a multi-item add. Letting this through would send an incomplete
         * request the customer believes is complete.
         */
        if (this.isActive && this.secondCheckedInput && !this.submitterAvailable) {
          this.blockSubmit(event);
          this.showError(
            'We could not add the second concession machine just now. Please remove it and try again, or contact us and we will add it for you.'
          );
          return;
        }

        this.hideError();
        this.lockSubmit();
      }

      blockSubmit(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      lockSubmit() {
        this.submitLocked = true;
        clearTimeout(this.submitLockTimer);
        // Safety valve in case neither cart event ever arrives.
        this.submitLockTimer = setTimeout(this.releaseSubmitLock, 8000);
      }

      releaseSubmitLock() {
        this.submitLocked = false;
        clearTimeout(this.submitLockTimer);
      }

      /* ---------------- Messaging ---------------- */

      showError(message) {
        const element = this.errorElement;
        if (element) {
          element.textContent = message || this.defaultErrorText;
          element.removeAttribute('hidden');
        }

        // Drives .jj-cp--invalid .jj-cp__error { display: block } as well as
        // the red card outline, so the message shows even if something else
        // in the theme re-applies the hidden attribute.
        this.classList.add('jj-cp--invalid');

        const first = this.inputs.find((input) => !input.disabled);
        if (first) first.focus({ preventScroll: true });

        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      hideError() {
        const element = this.errorElement;
        if (element) {
          element.setAttribute('hidden', '');
          element.textContent = this.defaultErrorText;
        }

        this.classList.remove('jj-cp--invalid');
      }
    }
  );
}
